import { useState } from 'react';
import {
  Card,
  CardHeader,
  Badge,
  Button,
  Input,
  Select,
  Modal,
  Toggle,
  Tabs,
  LoadingSkeleton,
} from '@nexora/design-system';
import {
  useFreightRules,
  useCreateFreightRule,
  useUpdateFreightRule,
  useDeleteFreightRule,
  type FreightRule,
  type FreightRuleInput,
} from './useFreightRules';
import {
  useDifalConfigs,
  useCreateDifalConfig,
  useUpdateDifalConfig,
  useDeleteDifalConfig,
  type DifalConfig,
  type DifalConfigInput,
} from './useDifalConfig';
import { formatDate } from '../../../shared/utils/format';

/* ───────────────────────────────────────────────────────────────────────────
   Portado de base-html, slug `erpone_gest_o_de_fretes_e_difal_reforma_tribut_ria`
   → Gestão de Fretes e DIFAL (Reforma Tributária) — CRUD /fiscal/freight-rules
   e /fiscal/difal-config (FiscalConfigController).

   Da referência vieram: as duas áreas (tabela de fretes e configuração de
   DIFAL), os campos de alíquota interestadual/interna e partilha, e o
   destaque "Ação Requerida: DIFAL" (derivado quando a alíquota do DIFAL
   difere da interestadual na regra de frete).

   Não vieram (não existem no contrato): documentos de transporte (CT-e),
   emissão de guias e a lista de CT-es — o módulo fiscal do backend não expõe
   CT-e; a tela cobre apenas regras de frete + configuração DIFAL.

   Rotas consumidas:
     - GET/POST /fiscal/freight-rules, PUT/DELETE /fiscal/freight-rules/:id
     - GET/POST /fiscal/difal-config, PUT/DELETE /fiscal/difal-config/:id
   ─────────────────────────────────────────────────────────────────────────── */

const UFS = [
  'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS',
  'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC',
  'SE', 'SP', 'TO',
];

const FRETE_TIPOS = [
  { value: 'CIF', label: 'CIF — incluído no preço' },
  { value: 'FOB', label: 'FOB — cobrado à parte' },
];

const BASES = [
  { value: 'VALOR', label: 'Por valor' },
  { value: 'PESO', label: 'Por peso (kg)' },
  { value: 'QUANTIDADE', label: 'Por quantidade' },
];

function formatPct(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return `${Number(v).toFixed(1).replace('.', ',')}%`;
}

function formatMoney(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
}

function emptyFreight(): FreightRuleInput {
  return {
    name: '',
    ufOrigem: 'SP',
    ufDestino: 'RJ',
    tipoFrete: 'FOB',
    baseCobranca: 'VALOR',
    enabled: true,
    effectiveSince: new Date().toISOString().split('T')[0] ?? '',
  };
}

function emptyDifal(): DifalConfigInput {
  return {
    name: '',
    ufOrigem: 'SP',
    ufDestino: 'RJ',
    aliquotaInterna: 0,
    aliquotaInterestadual: 0,
    percentualPartilhaOrigem: 40,
    percentualPartilhaDestino: 60,
    soConsumidorFinal: true,
    enabled: true,
    effectiveSince: new Date().toISOString().split('T')[0] ?? '',
  };
}

export function FreightDifalPage(): JSX.Element {
  const [tab, setTab] = useState('freight');

  /* ── Freight state ── */
  const [freightModal, setFreightModal] = useState(false);
  const [editingFreight, setEditingFreight] = useState<FreightRule | undefined>(undefined);
  const [freightForm, setFreightForm] = useState<FreightRuleInput>(emptyFreight());
  const [deleteFreight, setDeleteFreight] = useState<FreightRule | null>(null);

  const freightQuery = useFreightRules();
  const createFreight = useCreateFreightRule();
  const updateFreight = useUpdateFreightRule();
  const deleteFreightMut = useDeleteFreightRule();

  /* ── DIFAL state ── */
  const [difalModal, setDifalModal] = useState(false);
  const [editingDifal, setEditingDifal] = useState<DifalConfig | undefined>(undefined);
  const [difalForm, setDifalForm] = useState<DifalConfigInput>(emptyDifal());
  const [deleteDifal, setDeleteDifal] = useState<DifalConfig | null>(null);

  const difalQuery = useDifalConfigs();
  const createDifal = useCreateDifalConfig();
  const updateDifal = useUpdateDifalConfig();
  const deleteDifalMut = useDeleteDifalConfig();

  const freights = freightQuery.data?.data ?? [];
  const difals = difalQuery.data?.data ?? [];

  const savingFreight = createFreight.isPending || updateFreight.isPending;
  const savingDifal = createDifal.isPending || updateDifal.isPending;

  function openFreightCreate(): void {
    setEditingFreight(undefined);
    setFreightForm(emptyFreight());
    setFreightModal(true);
  }

  function openFreightEdit(r: FreightRule): void {
    setEditingFreight(r);
    setFreightForm({
      name: r.name,
      ...(r.description ? { description: r.description } : {}),
      ufOrigem: r.ufOrigem,
      ufDestino: r.ufDestino,
      ...(r.ncmPrefix ? { ncmPrefix: r.ncmPrefix } : {}),
      tipoFrete: r.tipoFrete,
      baseCobranca: r.baseCobranca,
      ...(r.aliquotaIcms !== null ? { aliquotaIcms: r.aliquotaIcms } : {}),
      ...(r.aliquotaDifal !== null ? { aliquotaDifal: r.aliquotaDifal } : {}),
      ...(r.valorMinimo !== null ? { valorMinimo: r.valorMinimo } : {}),
      ...(r.valorPorKg !== null ? { valorPorKg: r.valorPorKg } : {}),
      ...(r.valorPorUnidade !== null ? { valorPorUnidade: r.valorPorUnidade } : {}),
      ...(r.percentualValor !== null ? { percentualValor: r.percentualValor } : {}),
      ...(r.valorFixo !== null ? { valorFixo: r.valorFixo } : {}),
      enabled: r.enabled,
      effectiveSince: r.effectiveSince.split('T')[0] ?? r.effectiveSince,
      ...(r.effectiveUntil ? { effectiveUntil: r.effectiveUntil.split('T')[0] } : {}),
    });
    setFreightModal(true);
  }

  function handleSaveFreight(): void {
    if (editingFreight) {
      updateFreight.mutate({ id: editingFreight.id, ...freightForm }, { onSuccess: () => setFreightModal(false) });
    } else {
      createFreight.mutate(freightForm, { onSuccess: () => setFreightModal(false) });
    }
  }

  function openDifalCreate(): void {
    setEditingDifal(undefined);
    setDifalForm(emptyDifal());
    setDifalModal(true);
  }

  function openDifalEdit(c: DifalConfig): void {
    setEditingDifal(c);
    setDifalForm({
      name: c.name,
      ...(c.description ? { description: c.description } : {}),
      ufOrigem: c.ufOrigem,
      ufDestino: c.ufDestino,
      aliquotaInterna: c.aliquotaInterna,
      aliquotaInterestadual: c.aliquotaInterestadual,
      percentualPartilhaOrigem: c.percentualPartilhaOrigem,
      percentualPartilhaDestino: c.percentualPartilhaDestino,
      ...(c.fcpAliquota !== null ? { fcpAliquota: c.fcpAliquota } : {}),
      soConsumidorFinal: c.soConsumidorFinal,
      enabled: c.enabled,
      effectiveSince: c.effectiveSince.split('T')[0] ?? c.effectiveSince,
      ...(c.effectiveUntil ? { effectiveUntil: c.effectiveUntil.split('T')[0] } : {}),
    });
    setDifalModal(true);
  }

  function handleSaveDifal(): void {
    if (editingDifal) {
      updateDifal.mutate({ id: editingDifal.id, ...difalForm }, { onSuccess: () => setDifalModal(false) });
    } else {
      createDifal.mutate(difalForm, { onSuccess: () => setDifalModal(false) });
    }
  }

  const tabs = [
    { key: 'freight', label: `Fretes (${freightQuery.data?.total ?? 0})` },
    { key: 'difal', label: `DIFAL (${difalQuery.data?.total ?? 0})` },
  ];

  return (
    <div className="freight-difal-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">
            Gestão de Fretes e DIFAL (Reforma Tributária)
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Regras de frete por cenário UF/NCM e configuração de DIFAL (EC 87/2015 + EC 132/2023).
          </p>
        </div>
        <Button variant="primary" onClick={tab === 'freight' ? openFreightCreate : openDifalCreate}>
          + Nova {tab === 'freight' ? 'regra de frete' : 'configuração DIFAL'}
        </Button>
      </div>

      <Tabs tabs={tabs} value={tab} onChange={setTab} aria-label="Fretes e DIFAL" className="freight-tabs" />

      {/* ── Freight rules ── */}
      {tab === 'freight' && (
        <Card variant="outlined" padding="0">
          <CardHeader className="px-md pt-md" action={
            <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
              {freightQuery.data?.total ?? 0} regras
            </span>
          }>
            <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Tabela de fretes</h3>
          </CardHeader>
          {freightQuery.isLoading ? (
            <div className="fd-loading"><LoadingSkeleton count={5} height="44px" /></div>
          ) : freightQuery.error ? (
            <div className="fd-empty">
              <p style={{ fontWeight: 600, color: '#ba1a1a', margin: '0 0 0.5rem' }}>Erro ao carregar fretes</p>
              <p style={{ fontSize: '0.875rem', margin: '0 0 1rem' }}>
                {freightQuery.error instanceof Error ? freightQuery.error.message : 'Erro desconhecido'}
              </p>
              <Button variant="primary" onClick={() => freightQuery.refetch()}>Tentar novamente</Button>
            </div>
          ) : freights.length === 0 ? (
            <div className="fd-empty"><p className="m-0">Nenhuma regra de frete cadastrada.</p></div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="fiscal-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Cenário</th>
                    <th>Frete</th>
                    <th>Base</th>
                    <th align="right">ICMS</th>
                    <th align="right">DIFAL</th>
                    <th>Valores</th>
                    <th>Vigência</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {freights.map((r) => {
                    const difalPendente = r.aliquotaDifal !== null && r.aliquotaIcms !== null && r.aliquotaDifal !== r.aliquotaIcms;
                    return (
                      <tr key={r.id}>
                        <td>
                          <strong>{r.name}</strong>
                          {difalPendente && <span className="fd-flag">Ação Requerida: DIFAL</span>}
                          {r.description && <small className="cell-sub">{r.description}</small>}
                        </td>
                        <td className="tabular-nums">{r.ufOrigem} → {r.ufDestino}{r.ncmPrefix ? ` • NCM ${r.ncmPrefix}` : ''}</td>
                        <td><Badge size="sm" variant="neutral">{r.tipoFrete}</Badge></td>
                        <td>{r.baseCobranca}</td>
                        <td align="right" className="tabular-nums">{formatPct(r.aliquotaIcms)}</td>
                        <td align="right" className="tabular-nums">{formatPct(r.aliquotaDifal)}</td>
                        <td className="fd-values">
                          {r.valorFixo !== null && <span>{formatMoney(r.valorFixo)} fixo</span>}
                          {r.valorPorKg !== null && <span>{formatMoney(r.valorPorKg)}/kg</span>}
                          {r.valorPorUnidade !== null && <span>{formatMoney(r.valorPorUnidade)}/un</span>}
                          {r.percentualValor !== null && <span>{formatPct(r.percentualValor)} do valor</span>}
                          {r.valorMinimo !== null && <span>mín. {formatMoney(r.valorMinimo)}</span>}
                          {(r.valorFixo === null && r.valorPorKg === null && r.valorPorUnidade === null && r.percentualValor === null) && <span className="cell-muted">—</span>}
                        </td>
                        <td className="tabular-nums">{formatDate(r.effectiveSince)}{r.effectiveUntil ? ` – ${formatDate(r.effectiveUntil)}` : ' →'}</td>
                        <td><Badge size="sm" variant={r.enabled ? 'success' : 'neutral'}>{r.enabled ? 'Ativa' : 'Inativa'}</Badge></td>
                        <td>
                          <div className="cell-actions">
                            <button type="button" className="row-action" aria-label={`Editar ${r.name}`} title="Editar" onClick={() => openFreightEdit(r)}>
                              <span className="material-symbols-outlined" aria-hidden="true">edit</span>
                            </button>
                            <button type="button" className="row-action row-action--danger" aria-label={`Excluir ${r.name}`} title="Excluir" onClick={() => setDeleteFreight(r)}>
                              <span className="material-symbols-outlined" aria-hidden="true">delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* ── DIFAL configs ── */}
      {tab === 'difal' && (
        <Card variant="outlined" padding="0">
          <CardHeader className="px-md pt-md" action={
            <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
              {difalQuery.data?.total ?? 0} configurações
            </span>
          }>
            <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Configuração de DIFAL</h3>
          </CardHeader>
          {difalQuery.isLoading ? (
            <div className="fd-loading"><LoadingSkeleton count={5} height="44px" /></div>
          ) : difalQuery.error ? (
            <div className="fd-empty">
              <p style={{ fontWeight: 600, color: '#ba1a1a', margin: '0 0 0.5rem' }}>Erro ao carregar DIFAL</p>
              <p style={{ fontSize: '0.875rem', margin: '0 0 1rem' }}>
                {difalQuery.error instanceof Error ? difalQuery.error.message : 'Erro desconhecido'}
              </p>
              <Button variant="primary" onClick={() => difalQuery.refetch()}>Tentar novamente</Button>
            </div>
          ) : difals.length === 0 ? (
            <div className="fd-empty"><p className="m-0">Nenhuma configuração de DIFAL cadastrada.</p></div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="fiscal-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Cenário</th>
                    <th align="right">Alíq. interna</th>
                    <th align="right">Alíq. interestadual</th>
                    <th align="right">Partilha</th>
                    <th align="right">FCP</th>
                    <th>Consumidor final</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {difals.map((c) => (
                    <tr key={c.id}>
                      <td><strong>{c.name}</strong>{c.description && <small className="cell-sub">{c.description}</small>}</td>
                      <td className="tabular-nums">{c.ufOrigem} → {c.ufDestino}</td>
                      <td align="right" className="tabular-nums">{formatPct(c.aliquotaInterna)}</td>
                      <td align="right" className="tabular-nums">{formatPct(c.aliquotaInterestadual)}</td>
                      <td align="right" className="tabular-nums">
                        {c.percentualPartilhaOrigem}% / {c.percentualPartilhaDestino}%
                      </td>
                      <td align="right" className="tabular-nums">{formatPct(c.fcpAliquota)}</td>
                      <td>{c.soConsumidorFinal ? 'Sim' : 'Não'}</td>
                      <td><Badge size="sm" variant={c.enabled ? 'success' : 'neutral'}>{c.enabled ? 'Ativa' : 'Inativa'}</Badge></td>
                      <td>
                        <div className="cell-actions">
                          <button type="button" className="row-action" aria-label={`Editar ${c.name}`} title="Editar" onClick={() => openDifalEdit(c)}>
                            <span className="material-symbols-outlined" aria-hidden="true">edit</span>
                          </button>
                          <button type="button" className="row-action row-action--danger" aria-label={`Excluir ${c.name}`} title="Excluir" onClick={() => setDeleteDifal(c)}>
                            <span className="material-symbols-outlined" aria-hidden="true">delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* ── Freight modal ── */}
      <Modal isOpen={freightModal} onClose={() => setFreightModal(false)} title={editingFreight ? 'Editar regra de frete' : 'Nova regra de frete'} size="lg">
        <div className="modal-form">
          <Input label="Nome *" value={freightForm.name} onChange={(e) => setFreightForm({ ...freightForm, name: e.target.value })} placeholder="Ex.: Comércio de Peças RJ" />
          <Input label="Descrição" value={freightForm.description ?? ''} onChange={(e) => setFreightForm({ ...freightForm, description: e.target.value })} />
          <div className="modal-form__row">
            <Select label="UF de origem" value={freightForm.ufOrigem} onChange={(e) => setFreightForm({ ...freightForm, ufOrigem: e.target.value })}>
              {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
            </Select>
            <Select label="UF de destino" value={freightForm.ufDestino} onChange={(e) => setFreightForm({ ...freightForm, ufDestino: e.target.value })}>
              {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
            </Select>
            <Input label="Prefixo NCM (opcional)" value={freightForm.ncmPrefix ?? ''} onChange={(e) => setFreightForm({ ...freightForm, ncmPrefix: e.target.value.replace(/\D/g, '') || undefined })} />
          </div>
          <div className="modal-form__row">
            <Select label="Tipo de frete" value={freightForm.tipoFrete} onChange={(e) => setFreightForm({ ...freightForm, tipoFrete: e.target.value })}>
              {FRETE_TIPOS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </Select>
            <Select label="Base de cobrança" value={freightForm.baseCobranca} onChange={(e) => setFreightForm({ ...freightForm, baseCobranca: e.target.value })}>
              {BASES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
            </Select>
          </div>
          <div className="modal-form__row">
            <Input label="Alíquota ICMS (%)" type="number" step="0.01" value={freightForm.aliquotaIcms === undefined ? '' : String(freightForm.aliquotaIcms)} onChange={(e) => setFreightForm({ ...freightForm, aliquotaIcms: e.target.value ? Number(e.target.value) : undefined })} />
            <Input label="Alíquota DIFAL (%)" type="number" step="0.01" value={freightForm.aliquotaDifal === undefined ? '' : String(freightForm.aliquotaDifal)} onChange={(e) => setFreightForm({ ...freightForm, aliquotaDifal: e.target.value ? Number(e.target.value) : undefined })} />
          </div>
          <div className="modal-form__row">
            <Input label="Valor fixo (R$)" type="number" step="0.01" value={freightForm.valorFixo === undefined ? '' : String(freightForm.valorFixo)} onChange={(e) => setFreightForm({ ...freightForm, valorFixo: e.target.value ? Number(e.target.value) : undefined })} />
            <Input label="Valor por kg (R$)" type="number" step="0.01" value={freightForm.valorPorKg === undefined ? '' : String(freightForm.valorPorKg)} onChange={(e) => setFreightForm({ ...freightForm, valorPorKg: e.target.value ? Number(e.target.value) : undefined })} />
            <Input label="Valor por unidade (R$)" type="number" step="0.01" value={freightForm.valorPorUnidade === undefined ? '' : String(freightForm.valorPorUnidade)} onChange={(e) => setFreightForm({ ...freightForm, valorPorUnidade: e.target.value ? Number(e.target.value) : undefined })} />
          </div>
          <div className="modal-form__row">
            <Input label="Percentual do valor (%)" type="number" step="0.01" value={freightForm.percentualValor === undefined ? '' : String(freightForm.percentualValor)} onChange={(e) => setFreightForm({ ...freightForm, percentualValor: e.target.value ? Number(e.target.value) : undefined })} />
            <Input label="Valor mínimo (R$)" type="number" step="0.01" value={freightForm.valorMinimo === undefined ? '' : String(freightForm.valorMinimo)} onChange={(e) => setFreightForm({ ...freightForm, valorMinimo: e.target.value ? Number(e.target.value) : undefined })} />
          </div>
          <div className="modal-form__row">
            <Input label="Vigência a partir de *" type="date" value={freightForm.effectiveSince} onChange={(e) => setFreightForm({ ...freightForm, effectiveSince: e.target.value })} />
            <Input label="Vigência até (opcional)" type="date" value={freightForm.effectiveUntil ?? ''} onChange={(e) => setFreightForm({ ...freightForm, effectiveUntil: e.target.value || undefined })} />
          </div>
          <div className="modal-form__toggle">
            <span>Regra ativa?</span>
            <Toggle checked={freightForm.enabled ?? true} onChange={(c) => setFreightForm({ ...freightForm, enabled: c })} />
          </div>
        </div>
        <div className="modal-form__actions">
          <Button variant="ghost" onClick={() => setFreightModal(false)}>Cancelar</Button>
          <Button variant="primary" onClick={handleSaveFreight} disabled={savingFreight || !freightForm.name.trim()}>
            {savingFreight ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
        {(createFreight.isError || updateFreight.isError) && (
          <p style={{ color: '#ba1a1a', fontSize: '0.8125rem', margin: '0.75rem 0 0' }}>
            {(createFreight.error ?? updateFreight.error) instanceof Error ? (createFreight.error ?? updateFreight.error)?.message : 'Erro ao salvar'}
          </p>
        )}
      </Modal>

      {/* ── DIFAL modal ── */}
      <Modal isOpen={difalModal} onClose={() => setDifalModal(false)} title={editingDifal ? 'Editar configuração DIFAL' : 'Nova configuração DIFAL'} size="lg">
        <div className="modal-form">
          <Input label="Nome *" value={difalForm.name} onChange={(e) => setDifalForm({ ...difalForm, name: e.target.value })} placeholder="Ex.: DIFAL SP → RJ" />
          <Input label="Descrição" value={difalForm.description ?? ''} onChange={(e) => setDifalForm({ ...difalForm, description: e.target.value })} />
          <div className="modal-form__row">
            <Select label="UF de origem" value={difalForm.ufOrigem} onChange={(e) => setDifalForm({ ...difalForm, ufOrigem: e.target.value })}>
              {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
            </Select>
            <Select label="UF de destino" value={difalForm.ufDestino} onChange={(e) => setDifalForm({ ...difalForm, ufDestino: e.target.value })}>
              {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
            </Select>
          </div>
          <div className="modal-form__row">
            <Input label="Alíquota interna (%)" type="number" step="0.01" value={String(difalForm.aliquotaInterna)} onChange={(e) => setDifalForm({ ...difalForm, aliquotaInterna: Number(e.target.value) || 0 })} />
            <Input label="Alíquota interestadual (%)" type="number" step="0.01" value={String(difalForm.aliquotaInterestadual)} onChange={(e) => setDifalForm({ ...difalForm, aliquotaInterestadual: Number(e.target.value) || 0 })} />
            <Input label="FCP (%)" type="number" step="0.01" value={difalForm.fcpAliquota === undefined ? '' : String(difalForm.fcpAliquota)} onChange={(e) => setDifalForm({ ...difalForm, fcpAliquota: e.target.value ? Number(e.target.value) : undefined })} />
          </div>
          <div className="modal-form__row">
            <Input label="Partilha origem (%)" type="number" step="0.01" value={String(difalForm.percentualPartilhaOrigem ?? 40)} onChange={(e) => setDifalForm({ ...difalForm, percentualPartilhaOrigem: Number(e.target.value) || 0 })} />
            <Input label="Partilha destino (%)" type="number" step="0.01" value={String(difalForm.percentualPartilhaDestino ?? 60)} onChange={(e) => setDifalForm({ ...difalForm, percentualPartilhaDestino: Number(e.target.value) || 0 })} />
          </div>
          <div className="modal-form__row">
            <Input label="Vigência a partir de *" type="date" value={difalForm.effectiveSince} onChange={(e) => setDifalForm({ ...difalForm, effectiveSince: e.target.value })} />
            <Input label="Vigência até (opcional)" type="date" value={difalForm.effectiveUntil ?? ''} onChange={(e) => setDifalForm({ ...difalForm, effectiveUntil: e.target.value || undefined })} />
          </div>
          <div className="modal-form__toggle">
            <span>Somente consumidor final?</span>
            <Toggle checked={difalForm.soConsumidorFinal ?? true} onChange={(c) => setDifalForm({ ...difalForm, soConsumidorFinal: c })} />
          </div>
          <div className="modal-form__toggle">
            <span>Configuração ativa?</span>
            <Toggle checked={difalForm.enabled ?? true} onChange={(c) => setDifalForm({ ...difalForm, enabled: c })} />
          </div>
        </div>
        <div className="modal-form__actions">
          <Button variant="ghost" onClick={() => setDifalModal(false)}>Cancelar</Button>
          <Button variant="primary" onClick={handleSaveDifal} disabled={savingDifal || !difalForm.name.trim()}>
            {savingDifal ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
        {(createDifal.isError || updateDifal.isError) && (
          <p style={{ color: '#ba1a1a', fontSize: '0.8125rem', margin: '0.75rem 0 0' }}>
            {(createDifal.error ?? updateDifal.error) instanceof Error ? (createDifal.error ?? updateDifal.error)?.message : 'Erro ao salvar'}
          </p>
        )}
      </Modal>

      {/* ── Delete confirm: freight ── */}
      <Modal isOpen={deleteFreight !== null} onClose={() => setDeleteFreight(null)} title="Excluir regra de frete" size="sm">
        <p className="m-0" style={{ fontSize: '0.875rem' }}>Excluir <strong>{deleteFreight?.name}</strong>?</p>
        <div className="modal-form__actions">
          <Button variant="ghost" onClick={() => setDeleteFreight(null)}>Cancelar</Button>
          <Button variant="danger" onClick={() => deleteFreight && deleteFreightMut.mutate(deleteFreight.id, { onSuccess: () => setDeleteFreight(null) })} disabled={deleteFreightMut.isPending}>
            {deleteFreightMut.isPending ? 'Excluindo…' : 'Excluir'}
          </Button>
        </div>
      </Modal>

      {/* ── Delete confirm: difal ── */}
      <Modal isOpen={deleteDifal !== null} onClose={() => setDeleteDifal(null)} title="Excluir configuração DIFAL" size="sm">
        <p className="m-0" style={{ fontSize: '0.875rem' }}>Excluir <strong>{deleteDifal?.name}</strong>?</p>
        <div className="modal-form__actions">
          <Button variant="ghost" onClick={() => setDeleteDifal(null)}>Cancelar</Button>
          <Button variant="danger" onClick={() => deleteDifal && deleteDifalMut.mutate(deleteDifal.id, { onSuccess: () => setDeleteDifal(null) })} disabled={deleteDifalMut.isPending}>
            {deleteDifalMut.isPending ? 'Excluindo…' : 'Excluir'}
          </Button>
        </div>
      </Modal>

      <style>{`
        .freight-difal-page { padding: 1.5rem; }
        .freight-tabs { margin-bottom: 1rem; }
        .fd-loading, .fd-empty { padding: 2rem; text-align: center; color: #74777f; }
        .fd-empty { display: grid; gap: 0.5rem; justify-items: center; }
        .fd-flag { display: block; color: #ed6c02; font-size: 0.6875rem; font-weight: 600; margin-top: 0.25rem; }
        .fd-values { display: flex; flex-direction: column; gap: 0.125rem; font-size: 0.75rem; }
        .modal-form { display: grid; gap: 0.75rem; }
        .modal-form__row { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; }
        .modal-form__toggle { display: flex; align-items: center; justify-content: space-between; padding: 0.5rem 0; }
        .modal-form__actions { display: flex; justify-content: flex-end; gap: 0.5rem; margin-top: 1rem; }
        .fiscal-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .fiscal-table th {
          text-align: left; padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e;
          font-weight: 600; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em;
          border-bottom: 2px solid #e0e3e5; white-space: nowrap; position: sticky; top: 0; z-index: 1;
        }
        .fiscal-table td { padding: 0.625rem 1rem; border-bottom: 1px solid #eceef0; vertical-align: middle; }
        .fiscal-table tbody tr:hover { background: #f8fafc; }
        .tabular-nums { font-variant-numeric: tabular-nums; }
        .cell-sub { display: block; color: #74777f; font-size: 0.75rem; }
        .cell-muted { color: #74777f; }
        .cell-actions { display: flex; gap: 0.25rem; }
        .row-action { background: none; border: none; cursor: pointer; color: #43474e; padding: 0.25rem; border-radius: 6px; }
        .row-action:hover { background: #f2f4f6; }
        .row-action--danger:hover { color: #ba1a1a; background: #ffebee; }
        @media (max-width: 720px) { .modal-form__row { grid-template-columns: 1fr; } }
      `}</style>
    </div>
  );
}